import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom'; // Add this import
import { toast } from 'react-hot-toast'; // Add this import at the top
import { useSelector } from "react-redux"
import useFilesAndFolders from "@hooks/useFilesAndFolders"
import useUser from "@hooks/useUser"
import useAdAccounts from "@hooks/useAdAccounts";

import { Autocomplete, TextField, Box, Tabs, Tab, Chip } from '@mui/material';
import Navbar from '@components/Navbar/Navbar';
import { Button } from '@components/Button/Button';

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const CreativeSelectionPage = () => {
  const navigate = useNavigate(); // Add this
  const { handleGetUserById } = useUser();
  const userId = localStorage.getItem("userId");
  const { adAccounts, loading: adAccountsLoading, error: adAccountsError } = useAdAccounts(userId);

  const [selectedFolders, setSelectedFolders] = useState([]); // Changed from selectedFolder
  const [selectedAdAccountId, setSelectedAdAccountId] = useState('');
  const [syncedFolders, setSyncedFolders] = useState([]);
  const [inProgressFolders, setInProgressFolders] = useState(() => {
    const saved = localStorage.getItem('inProgressFolders');
    return saved ? JSON.parse(saved) : [];
  });
  const [tabValue, setTabValue] = useState(0);

  const { filesAndFolders } = useSelector((state) => state.filesAndFolders)
  const { user } = useSelector((state) => state.user)
  const { googleDriveLink } = user ?? {}

  const {
    handleGetFilesAndFolders,
    handleGetSyncedFolderIds,
    handleSyncAndProcessFolder,
    handleGetFolderSyncAndProcessStatus,
    handleGetFolderSyncAndProcessMultipleJobsStatus
  } = useFilesAndFolders()

  const [syncedFolderIds, setSyncedFolderIds] = useState([]);
  const [folderAdAccountMap, setFolderAdAccountMap] = useState({});

  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (userId) {
      handleGetUserById(userId).then(() => {
        if (googleDriveLink) {
          console.log("Fetching folders with link:", googleDriveLink);
          handleGetFilesAndFolders(googleDriveLink);
        }
      });

      handleGetSyncedFolderIds(userId).then((response) => {
        if (response) {
          const { processed_folder_ids, processed_folder_ad_account_map } = response;
          setSyncedFolderIds(processed_folder_ids);
          setFolderAdAccountMap(processed_folder_ad_account_map);
        }
      });
    }
  }, [googleDriveLink]);

  // Update syncedFolders whenever filesAndFolders or syncedFolderIds changes
  useEffect(() => {
    if (filesAndFolders && syncedFolderIds.length > 0) {
      const syncedFolderObjects = filesAndFolders.filter(folder =>
        syncedFolderIds.includes(folder.id) &&
        folder.mimeType === "application/vnd.google-apps.folder"
      );
      setSyncedFolders(syncedFolderObjects);
    }
  }, [filesAndFolders, syncedFolderIds]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const [folderJobMap, setFolderJobMap] = useState(() => {
    // Try to load from localStorage on initial render
    const saved = localStorage.getItem('folderJobMap');
    return saved ? JSON.parse(saved) : {};
  });

  const [jobStatusMap, setJobStatusMap] = useState(() => {
    const saved = localStorage.getItem('jobStatusMap');
    return saved ? JSON.parse(saved) : {};
  });

  const [expandedFolders, setExpandedFolders] = useState(() => {
    const saved = localStorage.getItem('expandedFolders');
    return saved ? JSON.parse(saved) : {};
  });

  // const userId = localStorage.getItem("userId");

  const handleSync = async () => {
    if (!selectedAdAccountId) {
      toast.error('Please select an ad account before syncing');
      return;
    }

    // Filter out folders that are already synced or in progress (keep this part)
    const newFolders = selectedFolders.filter(folder => {
      const isAlreadySynced = syncedFolders.find(f => f.id === folder.id);
      const isInProgress = inProgressFolders.find(f => f.id === folder.id);

      if (isAlreadySynced) {
        // check if the folder is synced to the same ad account, if not, show error
        const adAccount = folderAdAccountMap[folder.id];
        if (adAccount['fbAdAccountId'] !== selectedAdAccountId) {
          toast.error(`Folder "${folder.name}" is already synced to a different ad account`);
          return false;
        }
      }
      if (isInProgress) {
        toast.error(`Folder "${folder.name}" is currently syncing`);
        return false;
      }
      return true;
    });

    if (newFolders.length > 0) {
      setInProgressFolders(prev => [...prev, ...newFolders]);

      // Process each folder with a delay to avoid rate limiting
      for (let i = 0; i < newFolders.length; i++) {
        const folder = newFolders[i];

        // Wait 2 seconds between each folder processing
        if (i > 0) {
          await new Promise(resolve => setTimeout(resolve, 2000));
        }

        try {
          // Call API with the new structure
          // const response = await handleSyncAndProcessFolder(folder.id, userId);
          const response = await handleSyncAndProcessFolder(folder.id, userId, selectedAdAccountId);

          if (response && response.creative_job_objects && response.job_ids) {
            // Initialize status for each job ID
            const initialStatusMap = {};
            response.job_ids.forEach(jobId => {
              initialStatusMap[jobId] = "queued";
            });

            // Update job status map
            setJobStatusMap(prev => ({
              ...prev,
              ...initialStatusMap
            }));

            // Store the job objects array for this folder
            setFolderJobMap(prev => ({
              ...prev,
              [folder.id]: response.creative_job_objects
            }));

            // Auto-expand the folder in the UI
            setExpandedFolders(prev => ({
              ...prev,
              [folder.id]: true
            }));

            // Set up polling for multiple job statuses
            const statusInterval = setInterval(async () => {
              try {
                // Get status for all jobs in this folder
                const statusResponse = await handleGetFolderSyncAndProcessMultipleJobsStatus(response.job_ids);

                // Update status map with latest statuses
                setJobStatusMap(prev => ({
                  ...prev,
                  ...statusResponse
                }));

                // Check if all jobs are complete (finished or failed)
                const allJobsComplete = Object.values(statusResponse).every(
                  status => status === "finished" || status === "failed"
                );

                const allJobsSuccessful = Object.values(statusResponse).every(
                  status => status === "finished"
                );

                if (allJobsComplete) {
                  // Clear the interval
                  clearInterval(statusInterval);

                  // Remove from in-progress folders
                  setInProgressFolders(prev =>
                    prev.filter(f => f.id !== folder.id)
                  );

                  if (allJobsSuccessful) {
                    // Add to synced folders if all jobs successful
                    // setSyncedFolders(prev => {
                    //   const exists = prev.some(f => f.id === folder.id);
                    //   return exists ? prev : [...prev, folder];
                    // });

                    // Update synced folder IDs
                    // setSyncedFolderIds(prev => {
                    //   return prev.includes(folder.id) ? prev : [...prev, folder.id];
                    // });

                    handleGetSyncedFolderIds(userId).then((response) => {
                      if (response) {
                        const { processed_folder_ids, processed_folder_ad_account_map } = response;
                        setSyncedFolderIds(processed_folder_ids);
                        setFolderAdAccountMap(processed_folder_ad_account_map);
                      }
                    });

                    toast.success(`Folder "${folder.name}" successfully synced!`);
                  } else {
                    // Some jobs failed
                    const failedCount = Object.values(statusResponse).filter(
                      status => status === "failed"
                    ).length;

                    toast.error(
                      `Sync for "${folder.name}" completed with ${failedCount} failed files.`
                    );
                  }
                }
              } catch (error) {
                console.error(`Error checking job statuses:`, error);
              }
            }, 10000); // Check every 10 seconds
          } else {
            // No jobs returned, remove from in-progress
            setInProgressFolders(prev =>
              prev.filter(f => f.id !== folder.id)
            );
            toast.error(`Failed to start sync process for "${folder.name}"`);
          }
        } catch (error) {
          console.error(`Error syncing folder ${folder.name}:`, error);
          // Remove from in-progress on error
          setInProgressFolders(prev =>
            prev.filter(f => f.id !== folder.id)
          );
          toast.error(`Error syncing "${folder.name}": ${error.message || 'Unknown error'}`);
        }
      }
    }
  };

  const cleanupLocalStorage = () => {
    // Optional: Only clear if there are no in-progress folders
    if (inProgressFolders.length === 0) {
      localStorage.removeItem('folderJobMap');
      localStorage.removeItem('jobStatusMap');
      localStorage.removeItem('inProgressFolders');
      localStorage.removeItem('expandedFolders');
    }
  };

  // Update the navigation to go to launch-new-ad instead of direct navigation
  const handleNext = () => {
    if (inProgressFolders.length === 0) {
      cleanupLocalStorage();
    }
    navigate('/launch-new-ad', { state: { fromCreativeSelection: true } });
  };

  const isLoading = !filesAndFolders || !user;

  // Helper function to get file status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case "finished":
        return <span className="text-green-600">✓</span>;
      case "failed":
        return <span className="text-red-600">✗</span>;
      case "processing":
        return <div className="w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />;
      default: // queued or any other status
        return <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />;
    }
  };

  // Add effect to start polling for in-progress jobs on page load
  useEffect(() => {
    // Start polling for status of any in-progress jobs
    if (inProgressFolders.length > 0) {
      const allJobIds = [];
      
      // Collect all job IDs from in-progress folders
      Object.values(folderJobMap).forEach(jobs => {
        jobs.forEach(job => allJobIds.push(job.job_id));
      });
      
      if (allJobIds.length === 0) return;
      
      // Set up polling for all in-progress jobs
      const statusInterval = setInterval(async () => {
        try {
          // Get status for all jobs
          const statusResponse = await handleGetFolderSyncAndProcessMultipleJobsStatus(allJobIds);
          
          // Update status map with latest statuses
          setJobStatusMap(prev => ({
            ...prev,
            ...statusResponse
          }));
          
          // Check if any folders are complete
          inProgressFolders.forEach(folder => {
            const folderJobs = folderJobMap[folder.id] || [];
            const folderJobIds = folderJobs.map(job => job.job_id);
            
            // Filter status response for this folder's jobs
            const folderStatuses = {};
            folderJobIds.forEach(jobId => {
              if (statusResponse[jobId]) {
                folderStatuses[jobId] = statusResponse[jobId];
              }
            });
            
            if (Object.keys(folderStatuses).length === 0) return;
            
            // Check if all jobs for this folder are complete
            const allJobsComplete = Object.values(folderStatuses).every(
              status => status === "finished" || status === "failed"
            );
            
            const allJobsSuccessful = Object.values(folderStatuses).every(
              status => status === "finished"
            );
            
            if (allJobsComplete) {
              clearInterval(statusInterval);
              
              // Remove from in-progress folders
              setInProgressFolders(prev => 
                prev.filter(f => f.id !== folder.id)
              );
              
              if (allJobsSuccessful) {
                // Add to synced folders if all jobs successful
                // setSyncedFolders(prev => {
                //   const exists = prev.some(f => f.id === folder.id);
                //   return exists ? prev : [...prev, folder];
                // });
                
                // Update synced folder IDs
                // setSyncedFolderIds(prev => {
                //   return prev.includes(folder.id) ? prev : [...prev, folder.id];
                // });

                handleGetSyncedFolderIds(userId).then((response) => {
                  if (response) {
                    const { processed_folder_ids, processed_folder_ad_account_map } = response;
                    setSyncedFolderIds(processed_folder_ids);
                    setFolderAdAccountMap(processed_folder_ad_account_map);
                  }
                });
                
                toast.success(`Folder "${folder.name}" successfully synced!`);
              } else {
                // Some jobs failed
                const failedCount = Object.values(folderStatuses).filter(
                  status => status === "failed"
                ).length;
                
                toast.error(
                  `Sync for "${folder.name}" completed with ${failedCount} failed files.`
                );
              }
            }
          });
          
          // If no more in-progress folders, clear interval
          if (inProgressFolders.length === 0) {
            clearInterval(statusInterval);
          }
        } catch (error) {
          console.error('Error checking job statuses:', error);
        }
      }, 10000);
      
      // Clean up interval on component unmount
      return () => clearInterval(statusInterval);
    }
  }, []);  // Empty dependency array ensures this runs only once on mount

  // Add useEffect hooks to save state to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('folderJobMap', JSON.stringify(folderJobMap));
  }, [folderJobMap]);

  useEffect(() => {
    localStorage.setItem('jobStatusMap', JSON.stringify(jobStatusMap));
  }, [jobStatusMap]);

  useEffect(() => {
    localStorage.setItem('inProgressFolders', JSON.stringify(inProgressFolders));
  }, [inProgressFolders]);

  useEffect(() => {
    localStorage.setItem('expandedFolders', JSON.stringify(expandedFolders));
  }, [expandedFolders]);

  return (
    <div className="mx-auto flex flex-col py-10 px-6 bg-white min-h-screen max-w-7xl">
      <div className="relative z-50">  {/* Add this wrapper div */}
        {/* Update the Navbar section */}
        <Navbar
          title="Upload Creatives to Meta"
          onSaveAndNext={handleNext}
          isSaving={false}
          showBack={false}
          showSaveAndNext={true}
          saveButtonText="Launch Ads"
        />
      </div>

      <div className="flex flex-col gap-4 border bg-gray-50 rounded-xl p-6 shadow-inner mt-0 relative z-0">
        
        <div className="space-y-6">
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
            <div className="space-y-4">
              <div className="flex flex-col items-end gap-4">
                <Autocomplete
                    disablePortal
                    id="ad-account-select"
                    options={adAccounts}
                    getOptionLabel={(option) => option.fbAdAccountName}
                    value={adAccounts.find(account => account.fbAdAccountId === selectedAdAccountId) || null}
                    onChange={(event, newValue) => {
                        if (newValue) {
                        setSelectedAdAccountId(newValue.fbAdAccountId);
                        }
                    }}
                    renderInput={(params) => <TextField {...params} label="Select Ad Account" />}
                    loading={adAccountsLoading}
                    error={!!adAccountsError}
                    helperText={adAccountsError ? 'Error loading ad accounts' : ''}
                    required
                    className="w-full"
                />
                <Autocomplete
                  multiple
                  options={filesAndFolders?.filter(
                    (item) => item.mimeType === "application/vnd.google-apps.folder"
                  ) || []}
                  getOptionLabel={(option) => option.name || ''}
                  onChange={(_, values) => setSelectedFolders(values)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Folders"
                      placeholder={isLoading ? "Loading folders..." : "Search folders"}
                      variant="outlined"
                      fullWidth
                    />
                  )}
                  renderTags={(values, getTagProps) =>
                    values.map((option, index) => (
                      <Chip
                        key={option.id}
                        label={option.name}
                        {...getTagProps({ index })}
                        className="bg-indigo-100 text-indigo-700"
                      />
                    ))
                  }
                  loading={isLoading}
                  loadingText="Loading folders..."
                  noOptionsText={
                    !googleDriveLink
                      ? "Loading user data..."
                      : "No folders available"
                  }
                  className="w-full"
                />
                <div className="flex justify-end">
                  <Button
                    text="Sync Selected Folders"
                    onClick={handleSync}
                    disabled={selectedFolders.length === 0}
                    className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Status Tabs */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <Box sx={{
              borderBottom: 1,
              borderColor: 'divider',
              width: '100%',
              display: 'flex',
              justifyContent: 'center'
            }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                aria-label="folder status tabs"
                centered
                sx={{
                  '& .MuiTabs-flexContainer': {
                    justifyContent: 'center'
                  }
                }}
              >
                <Tab
                  label={
                    <div className="flex items-center gap-2">
                      <span>Synced Folders</span>
                      <span className="bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded-full text-sm">
                        {syncedFolders.length}
                      </span>
                    </div>
                  }
                  {...a11yProps(0)}
                />
                <Tab
                  label={
                    <div className="flex items-center gap-2">
                      <span>In Progress</span>
                      <span className="bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded-full text-sm">
                        {inProgressFolders.length}
                      </span>
                    </div>
                  }
                  {...a11yProps(1)}
                />
              </Tabs>
            </Box>

            <CustomTabPanel value={tabValue} index={0}>
              {syncedFolders.length === 0 ? (
                <p className="text-gray-500 text-sm">No folders synced yet</p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {syncedFolders.map(folder => (
                    <li
                      key={folder.id}
                      className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                    >
                      <span className="font-medium text-gray-700">{folder.name}</span> 
                      <span className="text-xs text-gray-500"> {folderAdAccountMap[folder.id]['fbAdAccountName'] + " (" + folderAdAccountMap[folder.id]['fbAdAccountId'] + ")" || 'N/A'}</span>
                      <span className="text-green-600 text-sm">Synced</span>
                    </li>
                  ))}
                </ul>
              )}
            </CustomTabPanel>

            <CustomTabPanel value={tabValue} index={1}>
              {inProgressFolders.length === 0 ? (
                <p className="text-gray-500 text-sm">No folders in progress</p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {inProgressFolders.map(folder => {
                    const folderJobs = folderJobMap[folder.id] || [];
                    const isExpanded = expandedFolders[folder.id] || false;

                    // Calculate folder progress
                    const totalFiles = folderJobs.length;
                    const finishedFiles = folderJobs.filter(
                      job => jobStatusMap[job.job_id] === "finished"
                    ).length;
                    const progress = totalFiles > 0 ? Math.round((finishedFiles / totalFiles) * 100) : 0;

                    return (
                      <li key={folder.id} className="flex flex-col">
                        <div
                          className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                          onClick={() => setExpandedFolders(prev => ({
                            ...prev,
                            [folder.id]: !isExpanded
                          }))}
                        >
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-700">{folder.name}</span>
                            {totalFiles > 0 && (
                              <span className="text-xs text-gray-500">
                                {finishedFiles} of {totalFiles} files processed
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            {totalFiles > 0 && (
                              <div className="w-32 bg-gray-200 rounded-full h-2.5">
                                <div
                                  className="bg-indigo-600 h-2.5 rounded-full"
                                  style={{ width: `${progress}%` }}
                                ></div>
                              </div>
                            )}
                            <div className="flex items-center gap-2 text-indigo-600">
                              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                              <span className="text-sm">Syncing...</span>
                            </div>
                            <span className="text-gray-400 text-lg">
                              {isExpanded ? '▼' : '▶'}
                            </span>
                          </div>
                        </div>

                        {/* Nested file status list */}
                        {isExpanded && folderJobs.length > 0 && (
                          <ul className="pl-8 pb-2 divide-y divide-gray-50">
                            {folderJobs.map(job => (
                              <li key={job.job_id} className="flex items-center justify-between py-2 px-4">
                                <span className="text-sm text-gray-600">{job.file_name}</span>
                                <div className="flex items-center gap-2">
                                  <span className="text-sm">
                                    {jobStatusMap[job.job_id] || "queued"}
                                  </span>
                                  {getStatusIcon(jobStatusMap[job.job_id])}
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </CustomTabPanel>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreativeSelectionPage;
